import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import type { FutureReservationsScope } from "@/hooks/useStayPlanContractConfig";

export interface CancelStayPlanInput {
  module: StayPlanModule;
  contractId: string;
  /** `all` também cancela as avulsas futuras que usam o plano. Só vale em creche. */
  futureScope?: FutureReservationsScope;
}

/** Cancela o contrato e zera o crédito de reposição; vendas e pagamentos existentes são preservados pela RPC. */
export async function cancelStayPlanContract({
  module,
  contractId,
  futureScope = "plan",
}: CancelStayPlanInput): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc("cancel_stay_plan_contract", {
    p_module: module,
    p_contract_id: contractId,
    p_future_scope: futureScope,
  });
  if (error) throw error;
}

export function useCancelStayPlan(): UseMutationResult<void, unknown, CancelStayPlanInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: cancelStayPlanContract,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      toast({ title: t("plans.cancelDone") });
    },
    onError: () => {
      toast({ variant: "destructive", title: t("plans.cancelFailed") });
    },
  });
}
